import NumberFormat from 'react-number-format';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd';

import {
  DatePickerInput,
  Form,
  NumberInput,
  PLATFORM_THEME_COLORS,
  SelectInput,
  TextInput,
  Typography,
} from '@thinknet-gmbh/platform-common-ui';

import { DEFAULT_NAME_FIELD_REGEX, HealthInsuranceTypesEnum } from '@thinknet-gmbh/platform-common';

import WithSwitcherFields from 'src/components/questionnaire-page/components/questionnaire-form/components/with-switcher-field/with-switcher-fields.components';

import { useGetAllHealthInsurance } from 'src/hooks/health-insurance/get-all-health-insurance.hook';

import { AutocompleteSelect } from 'src/components/autocomplete-select';

import {
  FieldsInfoWrapper,
  ComponentWrapper,
} from '../questionnaire-form/questionnaire-form.styles';

const PaymentAndInsurance = ({ form }: { form: FormInstance }) => {
  const { t, i18n } = useTranslation('employee');

  const { healthInsurance } = useGetAllHealthInsurance();

  const healthInsuranceOptions = healthInsurance.map((el: { name: string; _id: string }) => ({
    value: el._id,
    label: el.name,
  }));

  return (
    <ComponentWrapper>
      <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('paymentAndInsurance.labels.headerTitle')}
      </Typography>
      <FieldsInfoWrapper>
        <Form.Item
          name={['paymentAndInsurance', 'bankAccountHolder']}
          label={t('paymentAndInsurance.labels.accountHolder')}
          rules={[
            {
              required: true,
              message: t('paymentAndInsurance.validation.accountHolderRequired'),
            },
            {
              pattern: DEFAULT_NAME_FIELD_REGEX,
              message: t('common:validation.error.incorrectFormat'),
            },
          ]}
        >
          <TextInput
            maxLength={50}
            placeholder={t('paymentAndInsurance.placeholders.bankAccountHolder')}
          />
        </Form.Item>
        <Form.Item
          name={['paymentAndInsurance', 'iban']}
          label="IBAN"
          validateTrigger={[]}
          rules={[
            {
              required: true,
              message: t('paymentAndInsurance.validation.ibanRequired'),
            },
            { len: 22, message: t('paymentAndInsurance.validation.ibanInvalidLength') },
          ]}
        >
          <NumberFormat
            customInput={TextInput}
            prefix="DE"
            isNumericString
            maxLength={22}
            placeholder="DE89370400440532013000"
          />
        </Form.Item>
        <Form.Item
          name={['paymentAndInsurance', 'bic']}
          label="BIC"
          validateTrigger={[]}
          rules={[
            {
              required: true,
              message: t('paymentAndInsurance.validation.bicRequired'),
            },
            {
              pattern: /^[A-Za-z0-9]{8}([A-Za-z0-9]{3})?$/,
              message: t('paymentAndInsurance.validation.bicInvalidLength'),
            },
          ]}
        >
          <TextInput maxLength={11} placeholder="AABSDE31" />
        </Form.Item>

        <Form.Item
          name={['paymentAndInsurance', 'typeOfVisa']}
          label={t('paymentAndInsurance.labels.typeOfVisa')}
        >
          <TextInput placeholder="B-1" maxLength={50} />
        </Form.Item>
        <Form.Item
          name={['paymentAndInsurance', 'visaExpirationDate']}
          label={t('paymentAndInsurance.labels.visaExpiredDate')}
        >
          <DatePickerInput lang={i18n.language} format="DD.MM.YYYY" />
        </Form.Item>

        <Form.Item
          name={['paymentAndInsurance', 'nationalInsuranceNumber']}
          label={t('paymentAndInsurance.labels.insuranceNumber')}
          validateTrigger={[]}
          rules={[
            {
              pattern: /^\d{8}[A-Z]\d{3}$/,
              message: t('paymentAndInsurance.validation.invalidInsuranceNumber'),
            },
          ]}
        >
          <TextInput maxLength={12} placeholder="12345678A900" />
        </Form.Item>

        <Form.Item
          name={['paymentAndInsurance', 'taxId']}
          label={t('paymentAndInsurance.labels.taxId')}
          validateTrigger={[]}
          rules={[
            {
              required: true,
              message: t('customers:error.fieldIsRequired'),
            },
            {
              validator: (_, value) => !value || String(value).length === 11
                ? Promise.resolve()
                : Promise.reject(new Error(t('paymentAndInsurance.validation.taxIdInvalidLength'))),
            },
          ]}
        >
          <NumberInput
            maxLength={11}
            minLength={11}
            thousandSeparator={false}
            placeholder="3244422224865"
          />
        </Form.Item>

        <Form.Item
          name={['paymentAndInsurance', 'healthInsuranceType']}
          label={t('paymentAndInsurance.labels.healthInsuranceType')}
        >
          <SelectInput placeholder={t('paymentAndInsurance.labels.insurancePlan')}>
            {Object.keys(HealthInsuranceTypesEnum).map(option => (
              <SelectInput.Option value={option} key={option}>
                {t(`paymentAndInsurance.labels.healthInsuranceOptions.${option}`)}
              </SelectInput.Option>
            ))}
          </SelectInput>
        </Form.Item>
        <Form.Item
          name={['paymentAndInsurance', 'healthInsuranceId']}
          label={t('paymentAndInsurance.labels.healthInsuranceName')}
        >
          <AutocompleteSelect options={healthInsuranceOptions} placeholder="Humana" />
        </Form.Item>

        <WithSwitcherFields
          form={form}
          fieldName={'workPermissionExpireDate'}
          switcherName={'workPermissionNeeded'}
        />

        <WithSwitcherFields
          form={form}
          fieldName={'certificateOfStudyExpireDate'}
          switcherName={'hasCertificateOfStudy'}
        />

        <WithSwitcherFields
          form={form}
          fieldName={'residencePermissionExpireDate'}
          switcherName={'residencePermissionNeeded'}
        />
      </FieldsInfoWrapper>
    </ComponentWrapper>
  );
};

export default PaymentAndInsurance;
